"use client"

import { Box, Flex, HStack, Skeleton, Stack, VStack } from "@chakra-ui/react"

interface FeaturedWorkCardSkeletonProps {
  /**
   * Hint that this skeleton sits in a paper-coloured container (work index).
   * When true the inner card uses `bg.paper` to mirror the rendered card.
   */
  onPaper?: boolean
}

const SKILL_BADGE_SKELETON_WIDTHS = ["72px", "88px", "64px", "96px", "60px"]

const FeaturedWorkCardSkeleton = ({
  onPaper = false,
}: FeaturedWorkCardSkeletonProps) => {
  return (
    <Box
      _first={{
        borderTop: "1px solid {colors.border}",
      }}
      aria-busy="true"
      as="article"
      bgColor={onPaper ? "bg.paper" : undefined}
      borderBottom="1px solid {colors.border}"
      p={{ base: 1, md: 2, lg: 4 }}
      position="relative"
      rounded="0"
    >
      <Flex
        alignItems="stretch"
        flexDirection={{ base: "column", lg: "row" }}
        flexWrap="wrap"
        gap={{ base: "1.25rem", sm: "1.75rem" }}
      >
        <VStack
          alignItems="flex-start"
          flex="1"
          gap={{ base: "3", md: "3.5" }}
          minW="0"
          order={{ base: 2, sm: 2, lg: 1 }}
          pl={{ base: 0, md: 1, lg: 3 }}
          px={{ base: 3, md: 1, lg: 3 }}
          py={{ base: 3, lg: 2 }}
          w={{ base: "full", sm: "auto" }}
        >
          {/* eyebrow */}
          <Skeleton h={3} rounded="full" variant="shine" w="180px" />

          {/* title */}
          <Stack gap={2} w="full">
            <Skeleton
              h={{ base: 7, md: 8 }}
              rounded="lg"
              variant="shine"
              w="92%"
            />
            <Skeleton
              h={{ base: 7, md: 8 }}
              rounded="lg"
              variant="shine"
              w="68%"
            />
          </Stack>

          {/* description */}
          <Stack gap={2} w="full">
            <Skeleton h={3} rounded="full" variant="shine" w="96%" />
            <Skeleton h={3} rounded="full" variant="shine" w="78%" />
          </Stack>

          {/* evidence metric badge */}
          <Skeleton h={7} rounded="4xl" variant="shine" w="160px" />

          {/* skill badges */}
          <HStack flexWrap="wrap" gap="0.375rem" mb={2}>
            {SKILL_BADGE_SKELETON_WIDTHS.map((w) => (
              <Skeleton
                h={5}
                key={`skill-badge-skeleton-${w}`}
                rounded="full"
                variant="shine"
                w={w}
              />
            ))}
          </HStack>
        </VStack>

        <Box
          order={{ base: 1 }}
          pb={{ base: 0, md: 1, lg: 0 }}
          pt={{ base: 1, md: 0, lg: 0 }}
          px={{ base: 1, md: 0, lg: 0 }}
          w={{ base: "full", lg: "auto" }}
        >
          <Skeleton
            h={{ base: "220px", sm: "260px", lg: "280px", xl: "320px" }}
            rounded="0"
            variant="shine"
            w={{ base: "full", lg: "400px", xl: "460px" }}
          />
        </Box>
      </Flex>
    </Box>
  )
}

export default FeaturedWorkCardSkeleton
